import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { 
  HiPlus, 
  HiFolder, 
  HiClipboardList, 
  HiClock, 
  HiCheckCircle,
  HiExclamationCircle,
  HiUserGroup
} from 'react-icons/hi'
import { useProjects } from '../hooks/useProjects'
import { useTasks } from '../hooks/useTasks'
import { useAuth } from '../hooks/useAuth'
import { remindersApi } from '../api/client'
import { format, isToday, isTomorrow, isPast } from 'date-fns'
import CreateProjectModal from '../components/common/CreateProjectModal'

function StatCard({ icon: Icon, label, value, color }) {
  return (
    <div className="card p-3 sm:p-4 flex items-center gap-3 sm:gap-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5">
      <div className={`p-2.5 sm:p-3 rounded-lg ${color} shadow-sm`}>
        <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
      </div>
      <div className="min-w-0">
        <p className="text-xl sm:text-2xl font-bold text-surface-800 dark:text-surface-200">{value}</p>
        <p className="text-xs sm:text-sm text-surface-500 dark:text-surface-400 truncate">{label}</p>
      </div>
    </div>
  )
}

function TaskListItem({ task }) {
  const priorityColors = {
    critical: 'border-l-4 border-priority-critical',
    high: 'border-l-4 border-priority-high',
    medium: 'border-l-4 border-priority-medium',
    low: 'border-l-4 border-priority-low',
  }

  const isOverdue = task.due_date && isPast(new Date(task.due_date)) && task.status !== 'done'

  return (
    <Link
      to={`/tasks/${task.id}`}
      className={`block p-3 rounded-xl bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 hover:shadow-sm active:bg-surface-200 dark:active:bg-surface-600 transition-all duration-150 ${priorityColors[task.priority] || ''}`}
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-surface-800 dark:text-surface-200 truncate">{task.title}</h4>
          <p className="text-sm text-surface-500 dark:text-surface-400 truncate">{task.project?.name}</p>
        </div>
        {task.due_date && (
          <span className={`text-xs font-medium shrink-0 ml-2 px-2 py-0.5 rounded-full ${isOverdue ? 'text-red-700 bg-red-100 dark:bg-red-900/30 dark:text-red-300' : 'text-surface-600 bg-surface-200 dark:text-surface-400 dark:bg-surface-700'}`}>
            {isToday(new Date(task.due_date)) ? 'Today' :
             isTomorrow(new Date(task.due_date)) ? 'Tomorrow' :
             format(new Date(task.due_date), 'MMM d')}
          </span>
        )}
      </div>
    </Link>
  )
}

function ReminderItem({ reminder }) {
  const isOverdue = isPast(new Date(reminder.remind_at)) && reminder.status === 'pending'
  
  return (
    <div className={`p-3 rounded-xl bg-surface-50 dark:bg-surface-800 transition-all duration-150 hover:bg-surface-100 dark:hover:bg-surface-700 hover:shadow-sm ${isOverdue ? 'border-l-4 border-red-500' : ''}`}>
      <p className="font-medium text-surface-800 dark:text-surface-200">{reminder.message}</p>
      <div className="flex items-center gap-2 mt-1.5 text-xs text-surface-500 dark:text-surface-400">
        <HiClock className="w-3.5 h-3.5" />
        <span className={isOverdue ? 'text-red-600 font-medium' : ''}>
          {format(new Date(reminder.remind_at), 'MMM d, h:mm a')}
        </span>
      </div>
    </div>
  )
}

export default function Dashboard() {
  const { user } = useAuth()
  const { projects, loading: projectsLoading } = useProjects({ myProjects: true })
  const { tasks, loading: tasksLoading } = useTasks({ myTasks: true })
  const [reminders, setReminders] = useState([])
  const [showCreateProject, setShowCreateProject] = useState(false)

  useEffect(() => {
    async function fetchReminders() {
      try {
        const response = await remindersApi.list({ status: 'pending' })
        // Ensure we always have an array, even if API returns unexpected data
        setReminders(Array.isArray(response.data) ? response.data : [])
      } catch (err) {
        console.error('Failed to fetch reminders:', err)
      }
    }
    fetchReminders()
  }, [])

  // Separate owned vs. collaborated projects
  const ownedProjects = projects.filter(p => p.owner_id === user?.id)
  const collaboratingProjects = projects.filter(p => p.owner_id !== user?.id)

  const stats = {
    totalProjects: projects.length,
    ownedProjects: ownedProjects.length,
    activeTasks: tasks.filter(t => t.status !== 'done').length,
    dueTodayTasks: tasks.filter(t => t.due_date && isToday(new Date(t.due_date))).length,
    completedTasks: tasks.filter(t => t.status === 'done').length,
  }

  const upcomingTasks = tasks
    .filter(t => t.status !== 'done' && t.due_date)
    .sort((a, b) => new Date(a.due_date) - new Date(b.due_date))
    .slice(0, 5)

  const overdueTasks = tasks
    .filter(t => t.due_date && isPast(new Date(t.due_date)) && t.status !== 'done')
    .slice(0, 5)

  return (
    <div className="mx-auto max-w-[1400px] space-y-6 sm:space-y-8" data-tour="dashboard">
      {/* Header */}
      <div className="flex flex-col items-start gap-4 border-b border-surface-200 pb-5 dark:border-surface-700 sm:flex-row sm:items-center sm:justify-between xl:pb-6">
        <div>
          <h1 className="text-2xl font-bold text-surface-800 dark:text-surface-200">
            Welcome back, {user?.display_name?.split(' ')[0] || 'there'}!
          </h1>
          <p className="text-surface-500 dark:text-surface-400 mt-1">Here's what's happening with your projects.</p>
        </div>
        <div className="flex w-full items-center gap-2 sm:w-auto">
          <button
            onClick={() => setShowCreateProject(true)}
            className="btn btn-primary w-full sm:w-auto"
            data-tour="new-project"
          >
            <HiPlus className="w-5 h-5 mr-2" />
            New Project
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 xl:grid-cols-4 xl:gap-5">
        <StatCard
          icon={HiFolder}
          label="My Projects"
          value={stats.ownedProjects}
          color="bg-brand-500"
        />
        <StatCard
          icon={HiUserGroup}
          label="Collaborating"
          value={collaboratingProjects.length}
          color="bg-purple-500"
        />
        <StatCard
          icon={HiClipboardList}
          label="Active Tasks"
          value={stats.activeTasks}
          color="bg-yellow-500"
        />
        <StatCard
          icon={HiCheckCircle}
          label="Completed"
          value={stats.completedTasks}
          color="bg-green-500"
        />
      </div>

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3 xl:gap-6">
        {/* Overdue Tasks */}
        {overdueTasks.length > 0 && (
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <HiExclamationCircle className="w-5 h-5 text-red-500" />
              <h2 className="font-semibold text-surface-800 dark:text-surface-200">Overdue Tasks</h2>
            </div>
            <div className="space-y-2">
              {overdueTasks.map(task => (
                <TaskListItem key={task.id} task={task} />
              ))}
            </div>
          </div>
        )}

        {/* Upcoming Tasks */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-surface-800 dark:text-surface-200">Upcoming Tasks</h2>
            <Link to="/my-tasks" className="text-sm text-brand-600 hover:text-brand-700">
              View all
            </Link>
          </div>
          {upcomingTasks.length > 0 ? (
            <div className="space-y-2">
              {upcomingTasks.map(task => (
                <TaskListItem key={task.id} task={task} />
              ))}
            </div>
          ) : (
            <p className="text-center text-surface-500 dark:text-surface-400 py-8">No upcoming tasks</p>
          )}
        </div>

        {/* Reminders */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-surface-800 dark:text-surface-200">Upcoming Reminders</h2>
          </div>
          {reminders.length > 0 ? (
            <div className="space-y-2">
              {reminders.slice(0, 5).map(reminder => (
                <ReminderItem key={reminder.id} reminder={reminder} />
              ))}
            </div>
          ) : (
            <p className="text-center text-surface-500 dark:text-surface-400 py-8">No pending reminders</p>
          )}
        </div>

        {/* Recent Projects */}
        <div className="card p-4 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-surface-800 dark:text-surface-200">My Projects</h2>
          </div>
          {ownedProjects.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {ownedProjects.slice(0, 6).map(project => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-surface-800 dark:text-surface-200 truncate">{project.name}</h4>
                    <p className="text-xs text-surface-500 dark:text-surface-400">
                      {project.task_count || 0} tasks • {project.completed_task_count || 0} done
                      {project.member_count > 0 && (
                        <span> • <HiUserGroup className="w-3 h-3 inline -mt-0.5" /> {project.member_count}</span>
                      )}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-surface-500 dark:text-surface-400 mb-4">You haven't created any projects yet</p>
              <button
                onClick={() => setShowCreateProject(true)}
                className="btn btn-primary"
              >
                <HiPlus className="w-4 h-4 mr-2" />
                Create your first project
              </button>
            </div>
          )}
        </div>

        {/* Collaborating Projects */}
        {collaboratingProjects.length > 0 && (
          <div className="card p-4">
            <div className="flex items-center gap-2 mb-4">
              <HiUserGroup className="w-5 h-5 text-purple-500" />
              <h2 className="font-semibold text-surface-800 dark:text-surface-200">Collaborating On</h2>
            </div>
            <div className="space-y-2">
              {collaboratingProjects.slice(0, 5).map(project => (
                <Link
                  key={project.id}
                  to={`/projects/${project.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-surface-50 dark:bg-surface-800 hover:bg-surface-100 dark:hover:bg-surface-700 transition-colors"
                >
                  <span
                    className="w-3 h-3 rounded-full shrink-0"
                    style={{ backgroundColor: project.color || '#3b82f6' }}
                  />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-surface-800 dark:text-surface-200 truncate">{project.name}</h4>
                    <p className="text-xs text-surface-500 dark:text-surface-400">
                      {project.task_count || 0} tasks
                      {project.member_count > 0 && (
                        <span> • <HiUserGroup className="w-3 h-3 inline -mt-0.5" /> {project.member_count}</span>
                      )}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Project Modal */}
      {showCreateProject && (
        <CreateProjectModal
          onClose={() => setShowCreateProject(false)}
        />
      )}
    </div>
  )
}
